import assert from 'assert';

import {NameTemplateType, ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {getProcedureTypeForStudyTypeAllowNull} from 'studies/TypedStudyUtils';

import NameTemplateTypeConfiguration from './NameTemplateTypeConfiguration';

/**
 * Creates a name template type configuration that includes study type specific logic.
 *
 * This function is a convenience function for creating a name template type configuration that is specific to name template part types that depend on study type.
 */
export default function createStudyTypeNameTemplateTypeConfiguration(
  studyType: StudyType
): NameTemplateTypeConfiguration {
  const procedureType: ProcedureType | null = getProcedureTypeForStudyTypeAllowNull(studyType);

  // This function was introduced to allow for the StudyType configuration object to be used by StudyType.CUSTOM.
  // This is necessary because StudyType.CUSTOM does not have a procedure type.
  const getProcedureString = (stringCase?: StringCase): string => {
    assert(
      procedureType != null,
      'Expected procedure type to be defined in order to compute the procedure UI string.'
    );
    return getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {stringCase});
  };

  function getNameTemplateTypeFriendlyName(nameTemplateType: NameTemplateType): string | null {
    if (nameTemplateType === NameTemplateType.PROCEDURE_RUN) {
      return `${getProcedureString(StringCase.LOWER)} run`;
    }
    return null;
  }

  function getDisplayBannerText(nameTemplateType: NameTemplateType): string | null {
    if (nameTemplateType !== NameTemplateType.PROCEDURE_RUN || procedureType == null) {
      return null;
    }
    return `${getProcedureString()} run ID is a required name part and must be included within the ${getProcedureString(StringCase.LOWER)} run name template.`;
  }

  return {
    getNameTemplateTypeFriendlyName,
    getDisplayBannerText,
  };
}
